import React from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { AuthenticationFlowType } from '../../../../../typings/api/skymap/rest/v1/.common';
import { RedirectMicrosoftSsoQueryParameters } from '../../../../../typings/api/skymap/rest/v1/auth';
import { getZitadelUserManager } from '../../../services/authentication/zitadel-user-manager';
import { SkyMapAxiosServiceFactory } from '../../../services/axios/skymap-axios-service-factory';
import { emailRegex } from '../../../utils/text/validation';
import { isDefined } from '../../../utils/variables';
import { authHook } from '../../hooks/use-auth';
import { useLanguage } from '../../hooks/use-language';
import { isSessionExpiredSignIn } from '../../routing/session-expired';
import { useOptionalAuthState } from '../../state/auth-state';
import { reset } from '../../utils/styled-reset';
import { Button } from '../button/button';
import { HyperlinkStyled } from '../hyperlink/hyperlink';
import { InfoBox } from '../info-box/info-box';
import { LabelledContainer } from '../labelled-container/labelled-container';
import { LanguageSwitcher } from '../language-switcher/language-switcher';
import { Stack } from '../stack/stack';
import { TextBox } from '../text-box/text-box';
import { SsoActivationDialog } from './sso-activation-dialog';
import {
  DetermineAuthFlowResponse,
  SignInResponse,
  useAuthenticationFlowQuery,
} from './use-authentication-flow-query';

export type Form = {
  email: string;
  password: string;
};

export type SignInNavigateDestination = 'redirect' | 'requestpasswordchange';

type Props = {
  onNavigate: (destination: SignInNavigateDestination) => Promise<void> | void;
};

const SignInForm = ({ onNavigate }: Props) => {
  const { selectDefaultLanguage } = useLanguage();
  const { t } = useTranslation();
  const { saveToken } = authHook.useAuth();
  const authState = useOptionalAuthState();

  const navigate = React.useCallback(
    async (destination: SignInNavigateDestination) => {
      await onNavigate(destination);
    },
    [onNavigate],
  );

  // The login page should set either the default or cookie language.
  React.useEffect(() => {
    void selectDefaultLanguage();
  }, [selectDefaultLanguage]);

  const [flowType, setFlowType] = React.useState<AuthenticationFlowType | null>(null);
  const [showSsoActivationDialog, setShowSsoActivationDialog] = React.useState(false);
  const [signInUnavailable, setSignInUnavailable] = React.useState(false);
  // Why the user is here, when they did not choose to be. Read once: it explains the last session,
  // and stops being the thing worth saying as soon as they try to start a new one.
  const [sessionExpired, setSessionExpired] = React.useState(isSessionExpiredSignIn);

  const [ssoRequiredDomain, setSsoRequiredDomain] = React.useState<string>('');

  const {
    formState: { errors },
    clearErrors: clearFormErrors,
    getValues,
    handleSubmit,
    register,
    reset: resetForm,
    setError,
    setValue,
    watch,
  } = useForm<Form>({
    defaultValues: {
      email: '',
      password: '',
    },

    shouldUnregister: true,
  });

  // The form library learns a field's value from the input event alone. Browser autofill and
  // password managers can set a value without raising one, so the field would display one address
  // while the form validates and submits another. Submit what the inputs display.
  const adoptDisplayedValues = React.useCallback(
    (form: HTMLFormElement) => {
      for (const name of ['email', 'password'] as const) {
        const input = form.elements.namedItem(name);
        if (input instanceof HTMLInputElement && input.value !== getValues(name)) {
          setValue(name, input.value);
        }
      }
    },
    [getValues, setValue],
  );

  const redirectToMicrosoftSignIn = React.useCallback(
    (purpose: RedirectMicrosoftSsoQueryParameters['purpose']) => {
      const authV1Service = SkyMapAxiosServiceFactory.instance.createAuthServiceV1();

      const url = authV1Service.getMicrosoftRedirectUrl({
        query: { purpose, email: watch('email') },
      });
      window.location.href = url;
    },
    [watch],
  );

  const redirectToZitadelSignIn = React.useCallback(async () => {
    try {
      await getZitadelUserManager().signinRedirect({
        login_hint: watch('email'),
      });
    } catch {
      // A missing or broken Zitadel configuration must not leave the user on a form that does
      // nothing. Show the "sign-in unavailable" message instead.
      setSignInUnavailable(true);
    }
  }, [watch]);

  const onSuccessDetermineFlow = React.useCallback(
    ({ data }: DetermineAuthFlowResponse) => {
      if (data.authenticationFlow === 'PASSWORD') {
        setFlowType('PASSWORD');
      } else if (data.authenticationFlow === 'MICROSOFT_ENTRA_ID') {
        redirectToMicrosoftSignIn('sign-in');
      } else if (data.authenticationFlow === 'ZITADEL') {
        // Setting the flow keeps the password field and the "continue" button hidden either way:
        // this user has no password to type here.
        setFlowType('ZITADEL');
        void redirectToZitadelSignIn();
      }
    },
    [redirectToMicrosoftSignIn, redirectToZitadelSignIn],
  );

  /**
   * Called when `signInMutation` succeeds:
   *  - Save token, redirect, etc.
   */
  const onSuccessSignIn = React.useCallback(
    async ({ data }: SignInResponse) => {
      if (authState) {
        await authState.login(data.token);
      } else {
        await saveToken(data.token);
      }
      await navigate('redirect');
      resetForm();
    },
    [authState, saveToken, navigate, resetForm],
  );

  // Our merged hook with two mutations:
  const {
    clearErrors: clearMutationErrors,
    determineAuthFlowMutation,
    signInMutation,
    hasError,
    buildErrorList,
    getErrorForPath,
    excludeErrorPaths,
    translatedErrorMessages,
  } = useAuthenticationFlowQuery({
    onSuccessDetermineFlow,
    onSuccessSignIn,
  });

  // If the backend returned general errors for `email` or `password`, set them here.
  // When there is no error for a field, clear it so the UI updates (e.g. after user retries).
  React.useEffect(() => {
    const emailMessage = getErrorForPath('body.email');
    const passwordMessage = getErrorForPath('body.password');
    if (emailMessage) {
      setError('email', { type: 'custom', message: emailMessage });
    } else {
      clearFormErrors('email');
    }
    if (passwordMessage) {
      setError('password', { type: 'custom', message: passwordMessage });
    } else {
      clearFormErrors('password');
    }
  }, [getErrorForPath, setError, clearFormErrors]);

  // Handle SSO required error
  React.useEffect(() => {
    // Check if there's an auth:sso_required error in the translated errors
    const hasSsoRequiredError = translatedErrorMessages.some(
      (error) => error.originalError?.code === 'auth:sso_required',
    );

    if (hasSsoRequiredError) {
      const email = watch('email');
      const domain = email.split('@')[1];
      if (domain) {
        setSsoRequiredDomain(domain);
        setShowSsoActivationDialog(true);
      }
    }
  }, [translatedErrorMessages, watch]);

  // Exclude default errors for "email" and "password" since we handle them above
  const excludeDefaultErrors = excludeErrorPaths(['body.email', 'body.password']);

  // Calls "determineAuthFlow" only with email.
  const onSubmitDetermineAuthenticationFlow = React.useCallback(
    (formData: Form) => {
      clearMutationErrors();
      clearFormErrors();
      setSignInUnavailable(false);
      setSessionExpired(false);
      determineAuthFlowMutation.mutate(formData.email);
    },
    [clearMutationErrors, clearFormErrors, determineAuthFlowMutation],
  );

  // Calls "signIn" with email+password.
  const onSubmitSignIn = React.useCallback(
    (formData: Form) => {
      clearMutationErrors();
      clearFormErrors();
      setSessionExpired(false);
      signInMutation.mutate(formData);
    },
    [clearMutationErrors, clearFormErrors, signInMutation],
  );

  const isSubmitting = determineAuthFlowMutation.isPending || signInMutation.isPending;

  return (
    <Component>
      <TitleRow>
        <h1>{t('signInForm.title', { ns: 'components' })}</h1>
        <LanguageSwitcher />
      </TitleRow>

      {sessionExpired && (
        <InfoBox bottomMargin={true} color="yellow" leftIcon={{ icon: ['fad', 'exclamation'] }}>
          {t('signInForm.sessionExpiredMessage', { ns: 'components' })}
        </InfoBox>
      )}

      <form
        onSubmit={(e) => {
          e.preventDefault();
          adoptDisplayedValues(e.currentTarget);
          void handleSubmit(
            isDefined(flowType) ? onSubmitSignIn : onSubmitDetermineAuthenticationFlow,
          )(e);
        }}
      >
        <Stack spacing={1}>
          <LabelledContainer
            required={true}
            text={t('signInForm.form.email', { ns: 'components' })}
          >
            {(formElementId) => (
              <TextBox
                {...register('email', {
                  required: true,
                  pattern: {
                    value: emailRegex,
                    message: t('signInForm.emailInvalidMessage', { ns: 'components' }),
                  },
                })}
                autoFocus={true}
                disabled={isDefined(flowType) || isSubmitting}
                enforceCase="lowercase"
                errorMessage={errors.email?.message}
                id={formElementId()}
              />
            )}
          </LabelledContainer>

          <StyledLabelledContainer
            required={isDefined(flowType)}
            text={t('signInForm.form.password', { ns: 'components' })}
            visible={flowType === 'PASSWORD'}
          >
            {(formElementId) => (
              <TextBox
                {...register('password', { required: isDefined(flowType) })}
                disabled={isSubmitting}
                errorMessage={errors.password?.message}
                id={formElementId()}
                type="password"
              />
            )}
          </StyledLabelledContainer>

          {signInUnavailable && (
            <Stack direction="column" spacing={1}>
              <InfoBox color="yellow" leftIcon={{ icon: ['fad', 'exclamation'] }} topMargin={true}>
                {t('signInForm.signInUnavailableMessage', { ns: 'components' })}
              </InfoBox>
              <Stack direction="row" justifyContent="flex-start">
                <Button
                  color="secondary"
                  type="button"
                  variant="contained"
                  onClick={() => {
                    setSignInUnavailable(false);
                    setFlowType(null);
                  }}
                >
                  {t('signInForm.backButton', { ns: 'components' })}
                </Button>
              </Stack>
            </Stack>
          )}

          {/* If there are other errors (not email/password), show them */}
          {hasError(excludeDefaultErrors) && buildErrorList({ filter: excludeDefaultErrors })}

          {flowType === 'PASSWORD' && (
            <Stack direction="column" spacing={1}>
              <Stack
                direction="row"
                justifyContent="space-between"
                margin="1em 0 0 0"
                spacing={0.25}
              >
                <Button
                  color="secondary"
                  loading={signInMutation.isPending}
                  type="button"
                  variant="contained"
                  onClick={() => setFlowType(null)}
                >
                  {t('signInForm.backButton', { ns: 'components' })}
                </Button>
                <Button
                  autoFocus={true}
                  color="primary"
                  loading={signInMutation.isPending}
                  type="submit"
                  variant="contained"
                >
                  {t('signInForm.loginButton', { ns: 'components' })}
                </Button>
              </Stack>
              <HyperlinkStyled
                onClick={isSubmitting ? undefined : () => void navigate('requestpasswordchange')}
              >
                {t('signInForm.forgotPasswordButton', { ns: 'components' })}
              </HyperlinkStyled>
            </Stack>
          )}

          {!isDefined(flowType) && (
            <Stack direction="row" justifyContent="flex-end" margin="1em 0 0 0">
              <Button
                color="primary"
                loading={determineAuthFlowMutation.isPending}
                type="submit"
                variant="contained"
                width={'100%'}
              >
                {t('continue', { ns: 'common' })}
              </Button>
            </Stack>
          )}
        </Stack>
      </form>

      {showSsoActivationDialog && (
        <SsoActivationDialog
          domain={ssoRequiredDomain}
          onActivateSso={() => redirectToMicrosoftSignIn('connect-existing')}
          onClose={() => setShowSsoActivationDialog(false)}
        />
      )}
    </Component>
  );
};

const StyledLabelledContainer = styled(LabelledContainer)<{ visible: boolean }>`
  display: ${(props) => (props.visible ? 'block' : 'none')};
`;

const TitleRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 1em;
`;

const Component = styled.div`
  ${reset}
  font-size: 14px;

  h1 {
    font-size: 1.2em;
    font-weight: 500;
    margin: 0;
  }
`;

export { SignInForm };
